interface VisitorIntent {
  session_id: string;
  time_on_site: number;
  scroll_depth: number;
  hovered_categories: string[];
  clicked_products: string[];
  device_type: 'mobile' | 'tablet' | 'desktop';
  is_returning: boolean;
  last_quiz_category?: string;
  top_category?: string;
  last_name?: string;
}

// Throttle function - max 1 execution per delay ms
const throttle = (fn: Function, delay: number) => {
  let lastCall = 0;
  return (...args: any[]) => {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      fn(...args);
    }
  };
};

// Debounce function - delay execution until delay ms after last call
const debounce = (fn: Function, delay: number) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

export const getVisitorIntent = (): VisitorIntent => {
  const stored = localStorage.getItem('visitor_intent');
  if (stored) {
    return JSON.parse(stored);
  }
  
  return {
    session_id: crypto.randomUUID(),
    time_on_site: 0,
    scroll_depth: 0,
    hovered_categories: [],
    clicked_products: [],
    device_type: getDeviceType(),
    is_returning: false,
  };
};

export const updateVisitorIntent = (updates: Partial<VisitorIntent>) => {
  const current = getVisitorIntent();
  const updated = { ...current, ...updates };
  localStorage.setItem('visitor_intent', JSON.stringify(updated));
};

export const trackCategoryHover = (category: string) => {
  const intent = getVisitorIntent();
  if (!intent.hovered_categories.includes(category)) {
    intent.hovered_categories.push(category);
    updateVisitorIntent(intent);
  }
};

export const trackProductClick = (productId: string) => {
  const intent = getVisitorIntent();
  if (!intent.clicked_products.includes(productId)) {
    intent.clicked_products.push(productId);
    updateVisitorIntent(intent);
  }
};

export const calculateIntentScores = () => {
  const intent = getVisitorIntent();
  const scores = {
    private_loan: 0,
    credit_card: 0,
    savings: 0,
    business_loan: 0
  };

  // Hover = mild interest (2p each)
  intent.hovered_categories.forEach(cat => {
    if (cat in scores) {
      scores[cat as keyof typeof scores] += 2;
    }
  });

  // Time on site adds general interest
  if (intent.time_on_site > 30) {
    const topCategory = intent.top_category as keyof typeof scores;
    if (topCategory && topCategory in scores) {
      scores[topCategory] += 15;
    }
  }

  return scores;
};

const getDeviceType = (): 'mobile' | 'tablet' | 'desktop' => {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

export const initVisitorTracking = () => {
  // Mark as returning visitor if they've been here before
  const intent = getVisitorIntent();
  if (localStorage.getItem('has_visited')) {
    updateVisitorIntent({ is_returning: true });
  } else {
    localStorage.setItem('has_visited', 'true');
  }

  // Track time on site
  const startTime = Date.now();
  window.addEventListener('beforeunload', () => {
    const timeOnSite = Math.floor((Date.now() - startTime) / 1000);
    updateVisitorIntent({ time_on_site: timeOnSite });
  });

  // Track scroll depth with throttling and debouncing
  let maxScroll = 0;
  const saveScrollDepth = debounce((depth: number) => {
    updateVisitorIntent({ scroll_depth: depth });
  }, 500); // Save to localStorage max once per 500ms

  const handleScroll = throttle(() => {
    const scrollPercentage = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
    if (scrollPercentage > maxScroll) {
      maxScroll = scrollPercentage;
      saveScrollDepth(Math.floor(maxScroll));
    }
  }, 200); // Check scroll max 5 times per second

  window.addEventListener('scroll', handleScroll, { passive: true });
};
